import { Box, Typography } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';

import { ProgressBar } from '@/components';
import { Routes } from '@/constants';
import { Link } from '@/containers';
import { CheckmarkIcon } from '@/icons';
import { pinnedLpStar } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import { LearningPathWithDuration, Referrer } from '@/types';
import { createRouteWithParams } from '@/utils';

import {
  LearningPathCardDimensions,
  LearningPathCardVariants,
} from './constants';
import {
  CreditsDurationInfo,
  EpisodeCountWithCompletions,
} from './CreditsDurationInfo';

const { height, coverImageWidth, coverImageHeight } =
  LearningPathCardDimensions[LearningPathCardVariants.LIST];

export const LearningPathListCard = ({
  learningPath,
  referrer,
  userOrganizationName,
}: {
  learningPath: LearningPathWithDuration;
  referrer: Referrer;
  userOrganizationName?: string;
}) => {
  const { pathname, search } = useLocation();
  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.LEARNING_PATH_CARD,
    variants: {
      ...referrer.variants,
      lpCardVariant: LearningPathCardVariants.LIST,
    },
  };

  const { ref } = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange: inView => {
      if (inView) {
        AnalyticsService.learningPathImpression({
          learningPathId: learningPath.groupId,
          location,
          referrer,
        });
      }
    },
  });

  const episodeCount = learningPath.episodes.length || 0;
  const progressFraction = learningPath.duration
    ? Math.min(learningPath.durationPlayed / learningPath.duration, 1)
    : 0;

  return (
    <Box ref={ref}>
      <Link
        variant="plain"
        to={createRouteWithParams(Routes.LEARNING_PATH, {
          learningPathId: learningPath.groupId,
        })}
        onClick={() => {
          AnalyticsService.learningPathCardClicked({
            learningPathId: learningPath.groupId,
            location,
            referrer,
            episodesCompleted: learningPath.completions,
            isCompleted: learningPath.isCompleted,
            topic: learningPath.topic?.name ?? '',
            isRecommended: learningPath.isPinnedByOrg,
          });
        }}
        state={{ referrer, from: { pathname, search } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 3,
            width: '100%',
            height,
            py: 1,
            px: 2,
            backgroundColor: 'white',
            border: 1,
            borderColor: 'secondary.medium',
            borderRadius: 2,
            '&:hover': {
              borderColor: 'secondary.active',
              backgroundColor: 'secondary.light',
            },
            transition: 'border .1s ease, background-color .1s ease',
            '&:hover .cover-image': { transform: 'scale(1.1)' },
          }}>
          <Box
            sx={{
              position: 'relative',
              width: coverImageWidth,
              height: coverImageHeight,
              overflow: 'hidden',
              borderRadius: '50%',
            }}>
            <Box
              sx={[
                {
                  width: '100%',
                  height: '100%',
                  transition: 'transform .25s ease-in-out',
                },
                learningPath.isCompleted && { filter: 'grayscale(1)' },
              ]}
              className="cover-image"
              component="img"
              src={learningPath.coverImage}
              alt="learning path cover"
            />
            {learningPath.isCompleted && (
              <>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'accent.main',
                  }}>
                  <CheckmarkIcon sx={{ width: 24, height: 24 }} />
                </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 2,
              minWidth: 0,
            }}>
            <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
              {learningPath.isPinnedByOrg && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                  }}>
                  <Box
                    component="img"
                    src={pinnedLpStar}
                    alt="pinned lp star"
                  />
                  <Typography
                    variant="eyebrowHeader2"
                    noWrap
                    sx={{
                      color: 'secondary.main',
                      textTransform: 'uppercase',
                      minWidth: 0,
                    }}>
                    {userOrganizationName} Recommended
                  </Typography>
                </Box>
              )}
              <Typography variant="body3SemiBold">
                {learningPath.title}
              </Typography>
              {learningPath.durationPlayed > 0 && (
                <Box
                  sx={{ position: 'absolute', top: '100%', mt: 1, width: 200 }}>
                  <ProgressBar
                    progressFraction={progressFraction}
                    skinny
                    progressColor={
                      learningPath.isCompleted
                        ? 'accent.main'
                        : 'secondary.main'
                    }
                  />
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
            <Typography variant="caption1Bold" color="text.main">
              {learningPath.topic?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
            <CreditsDurationInfo
              credits={learningPath.credits}
              duration={learningPath.duration}
              durationPlayed={learningPath.durationPlayed}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
            <EpisodeCountWithCompletions
              episodeCount={episodeCount}
              completions={learningPath.completions}
            />
          </Box>
        </Box>
      </Link>
    </Box>
  );
};
